<!-- =========================================================================================
    File Name: OnboardingSlideshow.vue
    Description: Full-page slideshow component for onboarding presentation mode
    ----------------------------------------------------------------------------------------
    Item Name: TCVelik - Onboarding Platform
    Author: TCVelik Team
========================================================================================== -->

<template>
  <div class="onboarding-slideshow-container">
    <!-- Full-screen slideshow -->
    <swiper v-if="hasValidData"
            :options="swiperOption"
            class="fullscreen-swiper"
            ref="slideshowSwiper"
            @slideChange="onSlideChange"
            @reachEnd="onReachEnd">
      <!-- Title Slide -->
      <swiper-slide class="title-slide">
        <div class="slide-content title-content">
          <div class="title-wrapper">
            <h1 class="main-title">{{ onboardingData.title }}</h1>
            <p class="main-description" v-if="onboardingData.description">
              {{ onboardingData.description }}
            </p>

          </div>
        </div>
      </swiper-slide>

      <!-- Step Slides -->
      <swiper-slide v-for="(step, index) in onboardingData.steps"
                    :key="step.id || index"
                    class="step-slide">
        <div class="slide-content step-content">
          <div class="step-header">
            <h2 class="step-title">
              {{ step.subtype === 'form-capture' ? 'Information needed' : step.title }}
            </h2>
          </div>

          <div class="step-body">
            <div class="step-description" v-if="step.subtype === 'form-capture'">
              You are required to fill in some information before proceeding.
            </div>
            <div class="step-description" 
                 v-else-if="step.description" 
                 v-html="step.description">
            </div>
            <div v-if="step.metadata.Source">
              <vs-button size="large" icon="code" @click="openCodeFullscreen(step.metadata.Source)">Show code</vs-button>
            </div>
            
            <div v-if="step.subtype === 'form-capture' && step.metadata.formId">
              <vs-button 
                @click="openFormPopup(step.metadata.formId)"
                :disabled="completedForms.has(step.metadata.formId)">
                {{ completedForms.has(step.metadata.formId) ? 'Form Completed ✓' : 'Open Form' }}
              </vs-button>
            </div>

            <div v-if="step.subtype === 'assign-sop' && step.metadata.sopId">
              <div class="assign-sop-step">
                <!-- If assignment already submitted in this session, show success message -->
                <div v-if="isAssignmentSubmitted(step.metadata.sopId)" class="assign-sop-success">
                  <div class="success-message">
                    <i class="fas fa-check-circle text-success text-4xl mb-3"></i>
                    <h3 class="text-success mb-2">Assignment Created Successfully</h3>
                    <p class="text-gray-600">The next step has been assigned successfully.</p>
                  </div>
                </div>
                
                <!-- If user cannot change values and not submitted, show success message -->
                <div v-else-if="!step.metadata.allowUserToChangeValues" class="assign-sop-success">
                  <div class="success-message">
                    <i class="fas fa-check-circle text-success text-4xl mb-3"></i>
                    <h3 class="text-success mb-2">Assignment Created Successfully</h3>
                    <p class="text-gray-600">The next step has been assigned successfully.</p>
                  </div>
                </div>
                
                <!-- If user can change values and not submitted, show editable form -->
                <div v-else class="assign-sop-editable">
                  <h3>Next steps?</h3>
                  <p>Who and when should handle the next steps?</p>
                  
                  <!-- User Selection -->
                  <div class="form-field mb-4">
                    <label class="field-label">Assign To</label>
                    <vs-select 
                      v-model="assignSOPFormData.userId" 
                      label="Select User"
                      placeholder="Choose a user"
                      class="field-input"
                      required>
                      <vs-select-item 
                        v-for="user in availableUsers" 
                        :key="user.id"
                        :value="user.id"
                        :text="`${user.firstName || ''} ${user.lastName || ''} (${user.email})`" />
                    </vs-select>
                  </div>
                  
                  <!-- Due Date -->
                  <div class="form-field mb-4">
                    <label class="field-label">Due Date</label>
                    
                    <!-- Date Type Selection -->
                    <div class="date-type-selection mb-3">
                      <label class="radio-option">
                        <input 
                          type="radio" 
                          v-model="assignSOPFormData.dateType" 
                          value="absolute" 
                          name="dateTypeStep">
                        <span>Absolute Date</span>
                      </label>
                      <label class="radio-option">
                        <input 
                          type="radio" 
                          v-model="assignSOPFormData.dateType" 
                          value="relative" 
                          name="dateTypeStep">
                        <span>Relative Date</span>
                      </label>
                    </div>
                    
                    <!-- Absolute Date Input -->
                    <vs-input 
                      v-if="assignSOPFormData.dateType === 'absolute'"
                      v-model="assignSOPFormData.dueDate"
                      type="date"
                      label="Due Date"
                      placeholder="Select due date"
                      class="field-input"
                      required />
                    
                    <!-- Relative Date Input -->
                    <div v-if="assignSOPFormData.dateType === 'relative'" class="relative-date-input">
                      <div class="vx-row">
                        <div class="vx-col w-1/3">
                          <vs-input 
                            v-model="assignSOPFormData.relativeDays"
                            type="number"
                            label="Days"
                            placeholder="5"
                            class="field-input" />
                        </div>
                        <div class="vx-col w-1/3">
                          <span class="relative-text">days after</span>
                        </div>
                        <div class="vx-col w-1/3">
                          <vs-select 
                            v-model="assignSOPFormData.relativeEvent"
                            label="Event"
                            class="field-input">
                            <vs-select-item key="sop-completion" value="sop-completion" text="SOP Completion" />
                          </vs-select>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-actions">
                    <vs-button 
                      color="success" 
                      size="large"
                      :disabled="!assignSOPFormData.userId || (!assignSOPFormData.dueDate && assignSOPFormData.dateType === 'absolute') || (assignSOPFormData.dateType === 'relative' && !assignSOPFormData.relativeDays)"
                      @click="submitAssignSOPFromStep(step.metadata.sopId)">
                      Update Assignment
                    </vs-button>
                  </div>
                </div>
              </div>
            </div>

            <div class="step-images" v-if="step.images && step.images.length">
              <div class="image-gallery">
                <img v-for="(image, imgIndex) in step.images"
                     :key="imgIndex"
                     :src="image.src"
                     :alt="image.alt || step.title"
                     class="step-image"
                     @click="openImageModal(image.src)" />
              </div>
            </div>
          </div>

          <div class="slide-navigation">
            <div class="slide-counter">
              <span class="current-slide">{{ index + 2 }}</span>
              <span class="total-slides">/ {{ totalSlides }}</span>
            </div>
          </div>
        </div>
      </swiper-slide>

      <!-- Completion Slide -->
      <swiper-slide class="completion-slide">
        <div class="slide-content completion-content">
          <div class="completion-wrapper">
            <div class="completion-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h1 class="completion-title">Great job!</h1>
            <p class="completion-message">
              Would you like to record yourself as completed on this SOP?
            </p>
            <div class="completion-buttons">
              <button class="start-over-button"
                      @click="startOver">
                Start Over
              </button>
              <button class="completion-button"
                      @click="markAsCompleted">
                Mark Complete
              </button>
            </div>
            
            <!-- Steal this SOP section -->
            <div class="steal-sop-section">
              <p class="steal-sop-text">Want to keep this SOP for offline use?</p>
              <button class="steal-sop-button"
                      @click="stealThisSOP">
                <i class="fas fa-download"></i>
                Steal this SOP
              </button>
            </div>
          </div>
        </div>
      </swiper-slide>

      <!-- Navigation -->
      <div class="swiper-button-prev" slot="button-prev" v-show="currentSlide > 1">
        <i class="fas fa-chevron-left"></i>
      </div>
      <div class="swiper-button-next" 
           slot="button-next" 
           v-show="currentSlide < totalSlides"
           :class="{ 'disabled': !currentStepFormCompleted }">
        <i class="fas fa-chevron-right"></i>
      </div>

      <!-- Pagination - Removed -->
    </swiper>

    <!-- Loading State -->
    <div v-if="!hasValidData" class="loading-state">
      <div class="loading-content">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading presentation...</p>
      </div>
    </div>

    <!-- Floating Controls -->
    <div class="floating-controls">


      <button class="control-btn fullscreen-btn"
              @click="toggleFullscreen"
              title="Toggle Fullscreen">
        <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'" style="color: black;"></i>
      </button>
    </div>

    <!-- Image Modal -->
    <div v-if="showImageModal"
         class="image-modal"
         @click="closeImageModal">
      <div class="modal-content">
        <img :src="modalImageSrc" alt="Step Image" class="modal-image" />
        <button class="modal-close" @click="closeImageModal">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Form Modal -->
    <vs-popup title="Complete Form" :active.sync="showFormModal" fullscreen>
      <div class="form-modal-content">
        <div v-if="currentFormData" class="form-container">
          <h3 class="form-title">{{ currentFormData.title }}</h3>
          <div class="form-description" v-if="currentFormData.summary">
            {{ currentFormData.summary }}
          </div>
          
          <form @submit.prevent="submitForm" class="dynamic-form">
            <div v-for="field in currentFormData.fields" :key="field.id" class="form-field">
              <label :for="`field-${field.id}`" class="field-label">{{ field.title }}</label>
              
              <!-- Text Field -->
              <vs-input 
                v-if="field.type === 'text' || !field.type"
                :id="`field-${field.id}`"
                v-model="formSubmissionData[field.id]"
                :placeholder="field.placeholder || `Enter ${field.title}`"
                class="field-input"
                required />
              
              <!-- Yes/No Field -->
              <div v-else-if="field.type === 'yesno'" class="checkbox-field">
                <vs-checkbox 
                  :id="`field-${field.id}`"
                  v-model="formSubmissionData[field.id]"
                  :label="field.title"
                  class="field-input" />
              </div>
              
              <!-- DateTime Field -->
              <vs-input 
                v-else-if="field.type === 'datetime'"
                :id="`field-${field.id}`"
                v-model="formSubmissionData[field.id]"
                type="datetime-local"
                :placeholder="`Select ${field.title}`"
                class="field-input"
                required />
              
              <!-- Dropdown Field -->
              <vs-select 
                v-else-if="field.type === 'dropdown'"
                :id="`field-${field.id}`"
                v-model="formSubmissionData[field.id]"
                :label="field.title"
                :placeholder="`Select ${field.title}`"
                class="field-input"
                required>
                <vs-select-item 
                  v-for="option in field.options" 
                  :key="option.value"
                  :value="option.value" 
                  :text="option.text" />
              </vs-select>
              
              <!-- Email Field -->
              <vs-input 
                v-else-if="field.type === 'email'"
                :id="`field-${field.id}`"
                v-model="formSubmissionData[field.id]"
                type="email"
                :placeholder="`Enter ${field.title}`"
                class="field-input"
                required />
              
              <!-- File Upload Field -->
              <div v-else-if="field.type === 'file'" class="file-field">
                <vs-input 
                  :id="`field-${field.id}`"
                  type="file"
                  class="field-input"
                  disabled />
                <small class="text-gray-500 mt-1">Coming soon</small>
              </div>
              
              <!-- Default Text Field for unknown types -->
              <vs-input 
                v-else
                :id="`field-${field.id}`"
                v-model="formSubmissionData[field.id]"
                :placeholder="field.placeholder || `Enter ${field.title}`"
                class="field-input"
                required />
            </div>
            
            <div class="form-actions">
              <vs-button 
                :disabled="!isFormValid">
                Submit Form
              </vs-button>
              <vs-button 
                @click="closeFormModal"
                class="ml-2">
                Close
              </vs-button>
            </div>
          </form>
        </div>
        
        <div v-else class="form-loading">
          <vs-loading>Loading form...</vs-loading>
        </div>
      </div>
        </vs-popup>

    <!-- Assign SOP Modal -->
    <vs-popup title="Assign SOP" :active.sync="showAssignSOPModal" fullscreen>
      <div class="assign-sop-modal-content">
        <div v-if="currentAssignSOPData" class="assign-sop-container">
          <h3 class="assign-sop-title">{{ currentAssignSOPData.name }}</h3>
          <div class="assign-sop-description" v-if="currentAssignSOPData.description">
            {{ currentAssignSOPData.description }}
          </div>
          
          <form @submit.prevent="submitAssignSOP" class="assign-sop-form">
            <!-- SOP Selection -->
            <div class="form-field">
              <label class="field-label">SOP</label>
              <div class="sop-selection">
                <div v-if="!assignSOPFormData.selectedSOP" class="no-sop-selected">
                  <span class="no-sop-text">No SOP selected</span>
                  <a href="#" @click.prevent="openSOPSelector" class="change-link">Change</a>
                </div>
                <div v-else class="sop-selected">
                  <div class="sop-info">
                    <h4 class="sop-title">{{ assignSOPFormData.selectedSOP.name }}</h4>
                    <p class="sop-description">{{ assignSOPFormData.selectedSOP.description || 'No description available' }}</p>
                  </div>
                  <a href="#" @click.prevent="openSOPSelector" class="change-link">Change</a>
                </div>
              </div>
            </div>

            <!-- User Selection -->
            <div class="form-field">
              <label class="field-label">Assign To</label>
              <vs-select 
                v-model="assignSOPFormData.userId" 
                label="Select User"
                placeholder="Choose a user"
                class="field-input"
                required
                @change="onUserSelected"
                @input="onUserSelected">
                <vs-select-item 
                  v-for="user in availableUsers" 
                  :key="user.id"
                  :value="user.id"
                  :text="`${user.firstName || ''} ${user.lastName || ''} (${user.email})`" />
              </vs-select>
              <div v-if="availableUsers.length === 0" class="text-center text-gray-500">
                No users available
              </div>
              <div v-else class="text-sm text-gray-600">
                Found {{ availableUsers.length }} user(s)
              </div>
            </div>
            
            <!-- Due Date -->
            <div class="form-field">
              <label class="field-label">Due Date</label>
              
              <!-- Date Type Selection -->
              <div class="date-type-selection mb-3">
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="assignSOPFormData.dateType" 
                    value="absolute" 
                    name="dateTypeModal">
                  <span>Absolute Date</span>
                </label>
                <label class="radio-option">
                  <input 
                    type="radio" 
                    v-model="assignSOPFormData.dateType" 
                    value="relative" 
                    name="dateTypeModal">
                  <span>Relative Date</span>
                </label>
              </div>
              
              <!-- Absolute Date Input -->
              <vs-input 
                v-if="assignSOPFormData.dateType === 'absolute'"
                v-model="assignSOPFormData.dueDate"
                type="date"
                label="Due Date"
                placeholder="Select due date"
                class="field-input"
                required />
              
              <!-- Relative Date Input -->
              <div v-if="assignSOPFormData.dateType === 'relative'" class="relative-date-input">
                <div class="vx-row">
                  <div class="vx-col w-1/3">
                    <vs-input 
                      v-model="assignSOPFormData.relativeDays"
                      type="number"
                      label="Days"
                      placeholder="5"
                      class="field-input" />
                  </div>
                  <div class="vx-col w-1/3">
                    <span class="relative-text">days after</span>
                  </div>
                  <div class="vx-col w-1/3">
                    <vs-select 
                      v-model="assignSOPFormData.relativeEvent"
                      label="Event"
                      class="field-input">
                      <vs-select-item key="sop-completion" value="sop-completion" text="SOP Completion" />
                    </vs-select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Allow User to Change Values -->
            <div class="form-field">
              <div class="checkbox-field">
                <vs-checkbox 
                  v-model="assignSOPFormData.allowUserToChangeValues"
                  label="Allow user to change values"
                  class="field-input" />
              </div>
            </div>
            
            <div class="form-actions">
              <vs-button 
                type="submit" 
                color="success" 
                size="large"
                :disabled="!isAssignSOPFormValid">
                Create Assignment
              </vs-button>
              <vs-button 
                color="danger" 
                type="border" 
                size="large"
                @click="closeAssignSOPModal"
                class="ml-2">
                Cancel
              </vs-button>
            </div>
          </form>
        </div>
        
        <div v-else class="assign-sop-loading">
          <vs-loading>Loading SOP details...</vs-loading>
        </div>
      </div>
    </vs-popup>

    <!-- SOP Selector Modal -->
    <vs-popup title="Select SOP" :active.sync="showSOPSelector" fullscreen>
      <div class="sop-selector-content">
        <div v-if="availableSOPs.length > 0" class="sop-list-container">
          <div class="sop-list-header mb-4">
            <h3 class="text-lg font-semibold">Available SOPs</h3>
            <p class="text-gray-600">Select an SOP to assign</p>
          </div>

          <div class="sop-table-container">
            <vs-table class="sop-table" :data="availableSOPs" pagination max-items="10" search>
              <template slot="thead">
                <vs-th sort-key="id">ID</vs-th>
                <vs-th sort-key="name">Title</vs-th>
                <vs-th sort-key="description">Description</vs-th>
                <vs-th>Actions</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td :data="tr.id">
                    <div class="sop-id">{{ tr.id }}</div>
                  </vs-td>
                  
                  <vs-td :data="tr.name">
                    <div class="sop-name">
                      <strong>{{ tr.name }}</strong>
                    </div>
                  </vs-td>
                  
                  <vs-td :data="tr.description">
                    <div class="sop-description">
                      {{ tr.description || 'No description available' }}
                    </div>
                  </vs-td>
                  
                  <vs-td>
                    <vs-button 
                      @click="selectSOP(tr)"
                      color="primary" 
                      size="small">
                      Select
                    </vs-button>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
        </div>
        
        <div v-else class="empty-sops">
          <div class="text-center py-12 text-gray-500">
            <i class="fas fa-file-alt text-4xl mb-4"></i>
            <h3 class="text-xl font-semibold mb-2">No SOPs available</h3>
            <p class="text-gray-600">There are no SOPs available for assignment.</p>
          </div>
        </div>

        <div class="modal-actions">
          <vs-button @click="closeSOPSelector" color="warning" type="border">
            Cancel
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <div class="tcvelik-button">
      <button class="control-btn fullscreen-btn"
              @click="openInTCVelik"
              title="Open as document">
        <i class="fas fa-arrow-up-right-from-square" style="color: black;"></i>
      </button>
    </div>

    <!-- TCVelik Branding Link -->
    <div class="tcvelik-branding">
      <a href="https://tcvelik.com" target="_blank" rel="noopener noreferrer" class="branding-link">
        <span class="branding-text">Recorded with</span>
        <span class="branding-logo">TCVelik</span>
      </a>
    </div>

    <!-- Export Progress Popup -->
    <GenericProgressPopup :active="exportProgressActive"
                          :current-step="exportCurrentStep"
                          :total-steps="exportTotalSteps"
                          @close="exportProgressActive = false" />
  </div>
</template>

<script>
  import 'swiper/dist/css/swiper.min.css'
  import { swiper, swiperSlide } from 'vue-awesome-swiper'
  import MonacoEditor from '@/views/components/extra-components/editors/MonacoEditor.vue';
  import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';
  import TCVelikExportImportService from '@/services/TCVelikExportImportService.js';
  import GenericProgressPopup from '@/views/components/extra-components/generic-progress/GenericProgressPopup.vue';
  export default {
    name: 'OnboardingSlideshow',
    components: {
      swiper,
      swiperSlide,
      MonacoEditor,
      GenericProgressPopup
    },
    props: {
      onboardingData: {
        type: Object,
        required: true,
        default: () => ({
          title: '',
          description: '',
          steps: []
        })
      },
      autoPlay: {
        type: Boolean,
        default: false
      },
      autoPlayDelay: {
        type: Number,
        default: 5000
      }
    },
    data() {
      return {
        currentSlide: 1,
        showImageModal: false,
        modalImageSrc: '',
        isFullscreen: false,
        currentCode: "",
        InCodeFullscreenMode:false,
        resizeTimeout: null,
        showFormModal: false,
        currentFormId: null,
        currentFormData: null,
        completedForms: new Set(),
        formSubmissionData: {},
        showAssignSOPModal: false,
        currentAssignSOPData: null,
        assignSOPFormData: {
          userId: '',
          dueDate: null,
          selectedSOP: null,
          allowUserToChangeValues: false,
          dateType: 'absolute',
          relativeDays: 5,
          relativeEvent: 'sop-completion'
        },
        availableUsers: [],
        showSOPSelector: false,
        availableSOPs: [],
        submittedAssignments: new Set(), // Track submitted assignments to prevent duplicates
        currentProcessRun: null, // Current ProcessRun context
        processRunCreated: false, // Track if ProcessRun was created for this session
        currentProcessIdentifier: null, // Store Process Identifier across steps
        // Export progress tracking
        exportProgressActive: false,
        exportCurrentStep: 0,
        exportTotalSteps: 0
      }
    },
    computed: {
      totalSlides() {
        return this.onboardingData.steps.length + 2 // +1 for title slide, +1 for completion slide
      },

      isInIFrame() {
        return window.self !== window.top;
      },

      hasValidData() {
        return this.onboardingData &&
          this.onboardingData.steps &&
          Array.isArray(this.onboardingData.steps) &&
          this.onboardingData.steps.length > 0
      },

      currentStepHasFormCapture() {
        if (this.currentSlide <= 1 || this.currentSlide > this.onboardingData.steps.length + 1) {
          return false;
        }
        const stepIndex = this.currentSlide - 2; // Adjust for title slide
        const step = this.onboardingData.steps[stepIndex];
        return step && step.subtype === 'form-capture' && step.metadata && step.metadata.formId;
      },

      currentStepFormCompleted() {
        if (!this.currentStepHasFormCapture) {
          return true;
        }
        const stepIndex = this.currentSlide - 2;
        const step = this.onboardingData.steps[stepIndex];
        return this.completedForms.has(step.metadata.formId);
      },

      isFormValid() {
        if (!this.currentFormData || !this.currentFormData.fields) {
          return false;
        }
        return this.currentFormData.fields.every(field => {
          const value = this.formSubmissionData[field.id];
          
          // Yes/No fields can be false (unchecked) and still be valid
          if (field.type === 'yesno') {
            return value !== undefined && value !== null;
          }
          
          // File upload fields are not implemented yet, so skip validation
          if (field.type === 'file') {
            return true;
          }
          
          // For all other field types, check if value exists and is not empty
          return value && value.toString().trim() !== '';
        });
      },

      isAssignSOPFormValid() {
        const isValidAbsolute = this.assignSOPFormData.dateType === 'absolute' && this.assignSOPFormData.dueDate;
        const isValidRelative = this.assignSOPFormData.dateType === 'relative' && this.assignSOPFormData.relativeDays;
        
        const isValid = this.assignSOPFormData.userId && 
                       this.assignSOPFormData.selectedSOP &&
                       (isValidAbsolute || isValidRelative);
        console.log('Form validation:', {
          userId: this.assignSOPFormData.userId,
          dueDate: this.assignSOPFormData.dueDate,
          dateType: this.assignSOPFormData.dateType,
          relativeDays: this.assignSOPFormData.relativeDays,
          selectedSOP: this.assignSOPFormData.selectedSOP,
          allowUserToChangeValues: this.assignSOPFormData.allowUserToChangeValues,
          isValid: isValid
        });
        return isValid;
      },
      swiperOption() {
        return {
          direction: 'horizontal',
          slidesPerView: 1,
          spaceBetween: 0,
          centeredSlides: true,
          autoplay: this.autoPlay ? {
            delay: this.autoPlayDelay,
            disableOnInteraction: false
          } : false,
          pagination: false,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
          },
          keyboard: {
            enabled: true,
            onlyInViewport: true
          },
          mousewheel: {
            enabled: false
          },
          effect: 'slide',
          speed: 800,
          loop: false,
          grabCursor: true,
          preloadImages: true,
          lazy: {
            loadPrevNext: true
          }
        }
      }
    },
    watch: {
      // Watch for changes in body zoom
      '$route.query.preview': function (newPreview) {
        this.$nextTick(() => {
          this.adjustForZoom()
        })
      }
    },
    mounted() {
      this.setupKeyboardNavigation()
      this.setupFullscreenDetection()
      this.setupResizeDetection()
      this.adjustForZoom()
      // Load users for assign-sop steps
      this.loadAvailableUsers()

      // Check for existing ProcessRun ID from assignment metadata
      this.checkForExistingProcessRun()

      // Ensure swiper is properly initialized after data is ready
      this.$nextTick(() => {
        if (this.$refs.slideshowSwiper && this.$refs.slideshowSwiper.swiper) {
          this.$refs.slideshowSwiper.swiper.update()
          console.log('Swiper updated after mount')
        }
      })
    },
    watch: {
      'assignSOPFormData.userId'(newVal) {
      },
      'assignSOPFormData.dueDate'(newVal) {
        
      }
    },
    beforeDestroy() {
      this.cleanupEventListeners()
    },
    methods: {
      // ProcessRun Methods
      async checkForExistingProcessRun() {
        try {
          const currentUser = this.getCurrentUser();
          if (!currentUser) {
            await this.createProcessRunForOnboarding();
            return;
          }

          // Query for assignments for this user and onboarding
          const query = `
            query ReviewAssignmentsForCalendar {
              reviewAssignmentsForCalendar {
                id
                onboardingId
                userId
                customTitle
                dueForReview
                type
                processRunId
                processRun {
                  id
                  name
                  description
                  processType
                  status
                }
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query
          });

          if (response && response.data && response.data.data && response.data.data.reviewAssignmentsForCalendar) {
            const assignments = response.data.data.reviewAssignmentsForCalendar;
            
            // Find assignment for current user and this onboarding
            const userAssignment = assignments.find(assignment => 
              assignment.userId === currentUser.email && 
              assignment.onboardingId === this.onboardingData.id
            );

            if (userAssignment && userAssignment.processRunId && userAssignment.processRun) {
              // Join existing ProcessRun
              this.currentProcessRun = userAssignment.processRun;
              this.processRunCreated = true;
              return;
            }
          }

          // No existing assignment found, create new ProcessRun
          await this.createProcessRunForOnboarding();
        } catch (error) {
          // Fallback to creating new ProcessRun
          await this.createProcessRunForOnboarding();
        }
      },

      async findOrCreateProcessRun() {
        try {
          if (this.processRunCreated) {
            return; // Already created
          }

          const currentUser = this.getCurrentUser();
          if (!currentUser) {
            return;
          }

          // Try to get Process Identifier from current form submission
          const processIdentifier = this.extractProcessIdentifier();
          
          // First, try to find existing ProcessRun by ProcessIdentifier
          if (processIdentifier) {
            const existingProcessRun = await this.findProcessRunByIdentifier(processIdentifier);
            if (existingProcessRun) {
              console.log('Found existing ProcessRun:', existingProcessRun);
              this.currentProcessRun = existingProcessRun;
              this.processRunCreated = true;
              return;
            }
          }

          // If no existing ProcessRun found, create a new one
          await this.createNewProcessRun(processIdentifier, currentUser);
        } catch (error) {
          console.error('Error in findOrCreateProcessRun:', error);
          // Fallback to creating new ProcessRun
          await this.createNewProcessRun(null, this.getCurrentUser());
        }
      },

      async findProcessRunByIdentifier(processIdentifier) {
        try {
          const query = `
            query ProcessRunByIdentifier($processIdentifier: String!) {
              processRunByIdentifier(processIdentifier: $processIdentifier) {
                id
                name
                description
                processType
                status
                createdAt
                processData
                assignments {
                  id
                  contextData
                  onboardingSchedule {
                    id
                    customTitle
                    onboarding {
                      id
                      name
                    }
                  }
                }
                formSubmissions {
                  id
                  contextData
                  formSubmission {
                    id
                    submittedAt
                    form {
                      id
                      title
                    }
                  }
                }
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query,
            variables: {
              processIdentifier: processIdentifier
            }
          });

          if (response && response.data && response.data.data && response.data.data.processRunByIdentifier) {
            return response.data.data.processRunByIdentifier;
          }
          return null;
        } catch (error) {
          console.error('Error finding ProcessRun by identifier:', error);
          return null;
        }
      },

      async createNewProcessRun(processIdentifier, currentUser) {
        const identifierSuffix = processIdentifier ? ` - ${processIdentifier}` : ` - ${currentUser.email}`;
        
        const processRunName = `${this.onboardingData.title}${identifierSuffix}`;
        const processRunDescription = `Process run for onboarding: ${this.onboardingData.title}${processIdentifier ? ` (${processIdentifier})` : ''}`;
        const processType = 'OnboardingExecution';

        const processData = {
          processIdentifier: processIdentifier,
          onboardingId: this.onboardingData.id,
          onboardingTitle: this.onboardingData.title,
          startedBy: currentUser.email,
          startedAt: new Date().toISOString(),
          completedSOPs: [],
          formSubmissions: [],
          assignments: []
        };

        try {
          const mutation = `
            mutation CreateProcessRun($name: String!, $description: String!, $processType: String!, $processData: String) {
              createProcessRun(
                name: $name,
                description: $description,
                processType: $processType,
                processData: $processData
              ) {
                id
                name
                description
                processType
                status
                createdAt
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: {
              name: processRunName,
              description: processRunDescription,
              processType: processType,
              processData: JSON.stringify(processData)
            }
          });

          if (response && response.data && response.data.data && response.data.data.createProcessRun) {
            this.currentProcessRun = response.data.data.createProcessRun;
            this.processRunCreated = true;
            console.log('ProcessRun created successfully:', this.currentProcessRun);
          } else {
            console.error('Failed to create ProcessRun:', response);
          }
        } catch (error) {
          console.error('Error creating ProcessRun:', error);
        }
      },

      async createProcessRunForOnboarding() {
        // Legacy method - now redirects to findOrCreateProcessRun
        await this.findOrCreateProcessRun();
      },

      async updateProcessRunWithFormSubmission(formId, submissionResult) {
        try {
          // Update ProcessRun.ProcessData to include form submission
          const currentProcessData = this.currentProcessRun.processData ? 
            JSON.parse(this.currentProcessRun.processData) : {};
          
          if (!currentProcessData.formSubmissions) {
            currentProcessData.formSubmissions = [];
          }
          
          currentProcessData.formSubmissions.push({
            formId: formId,
            submissionId: submissionResult.id,
            submittedAt: submissionResult.submittedAt,
            submittedBy: submissionResult.submittedBy
          });
          
          // Update ProcessRun with new process data
          const mutation = `
            mutation UpdateProcessRun($processRunId: Int!, $processData: String) {
              updateProcessRun(processRunId: $processRunId, processData: $processData) {
                id
                processData
              }
            }
          `;
          
          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: {
              processRunId: parseInt(this.currentProcessRun.id),
              processData: JSON.stringify(currentProcessData)
            }
          });
          
          if (response && response.data && response.data.data && response.data.data.updateProcessRun) {
            this.currentProcessRun.processData = response.data.data.updateProcessRun.processData;
            console.log('ProcessRun updated with form submission');
          }
        } catch (error) {
          console.error('Error updating ProcessRun with form submission:', error);
        }
      },

      async updateProcessRunWithCompletedSOP(step, stepIndex) {
        try {
          // Update ProcessRun.ProcessData to include completed SOP
          const currentProcessData = this.currentProcessRun.processData ? 
            JSON.parse(this.currentProcessRun.processData) : {};
          
          if (!currentProcessData.completedSOPs) {
            currentProcessData.completedSOPs = [];
          }
          
          currentProcessData.completedSOPs.push({
            onboardingId: this.onboardingData.id,
            onboardingTitle: this.onboardingData.title,
            stepIndex: stepIndex,
            stepTitle: step.title,
            stepSubtype: step.subtype,
            completedAt: new Date().toISOString(),
            completedBy: this.getCurrentUser()?.email
          });
          
          // Update ProcessRun with new process data
          const mutation = `
            mutation UpdateProcessRun($processRunId: Int!, $processData: String) {
              updateProcessRun(processRunId: $processRunId, processData: $processData) {
                id
                processData
              }
            }
          `;
          
          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: {
              processRunId: parseInt(this.currentProcessRun.id),
              processData: JSON.stringify(currentProcessData)
            }
          });
          
          if (response && response.data && response.data.data && response.data.data.updateProcessRun) {
            this.currentProcessRun.processData = response.data.data.updateProcessRun.processData;
            console.log('ProcessRun updated with completed SOP');
          }
        } catch (error) {
          console.error('Error updating ProcessRun with completed SOP:', error);
        }
      },

      async getFormFieldValueFromProcessRun(formId, fieldId) {
        try {
          if (!this.currentProcessRun || !this.currentProcessRun.id) {
            console.warn('No current ProcessRun available');
            return null;
          }

          // Query for form submissions in the current ProcessRun
          const query = `
            query ProcessRunById($id: Int!) {
              processRunById(id: $id) {
                id
                formSubmissions {
                  id
                  contextData
                  formSubmission {
                    id
                    formId
                    submissionData
                    submittedAt
                    form {
                      id
                      title
                    }
                  }
                }
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query,
            variables: {
              id: parseInt(this.currentProcessRun.id)
            }
          });

          if (response && response.data && response.data.data && response.data.data.processRunById) {
            const processRun = response.data.data.processRunById;
            
            // Find the form submission for the specified form
            const formSubmission = processRun.formSubmissions.find(fs => 
              fs.formSubmission.formId === formId
            );

            if (formSubmission && formSubmission.formSubmission.submissionData) {
              try {
                const submissionData = JSON.parse(formSubmission.formSubmission.submissionData);
                
                // Find the field value by field ID
                const fieldValue = submissionData[fieldId];
                
                console.log('Found form field value:', {
                  formId,
                  fieldId,
                  fieldValue,
                  submissionData
                });
                
                return fieldValue;
              } catch (parseError) {
                console.error('Error parsing form submission data:', parseError);
              }
            } else {
              console.warn('Form submission not found for form', formId, 'in ProcessRun', this.currentProcessRun.id);
            }
          }

          return null;
        } catch (error) {
          console.error('Error getting form field value from ProcessRun:', error);
          return null;
        }
      },

      // Extract Process Identifier from current form submission
      extractProcessIdentifier() {
      
        // First check if we already have a stored Process Identifier
        if (this.currentProcessIdentifier) {
          console.log('Returning stored Process Identifier:', this.currentProcessIdentifier);
          return this.currentProcessIdentifier;
        }

        // If no stored identifier, try to extract from current form
        if (!this.currentFormId || !this.formSubmissionData || !this.currentFormData) {
          console.log('Missing form data, returning null');
          return null;
        }

        // Find the field marked as Process Identifier in the current form
        const processIdentifierField = this.currentFormData.fields.find(field => 
          field.isProcessIdentifier === true
        );

        console.log('processIdentifierField:', processIdentifierField);

        if (!processIdentifierField) {
          console.log('No Process Identifier field found');
          return null;
        }

        // Get the value from form submission data using the correct key
        const identifier = this.formSubmissionData[processIdentifierField.id] || null;
        
        console.log('Extracted identifier:', identifier);
        
        // Store the identifier for future use
        if (identifier) {
          this.currentProcessIdentifier = identifier;
          console.log('Stored Process Identifier:', this.currentProcessIdentifier);
        }
        
        return identifier;
      },

      // Update ProcessRun with Process Identifier (late binding)
      async updateProcessRunWithIdentifier(processIdentifier) {
        try {
          if (!this.currentProcessRun || !this.currentProcessRun.id) {
            console.warn('No current ProcessRun to update');
            return;
          }

          const currentUser = this.getCurrentUser();
          if (!currentUser) {
            console.warn('No current user found for ProcessRun update');
            return;
          }

          // Generate new name and description with Process Identifier
          const newName = `${this.onboardingData.title} - ${processIdentifier}`;
          const newDescription = `Process run for onboarding: ${this.onboardingData.title} (${processIdentifier})`;

          // Update ProcessRun metadata
          const updatedProcessData = {
            onboardingId: this.onboardingData.id,
            onboardingTitle: this.onboardingData.title,
            startedBy: currentUser.email,
            startedAt: this.currentProcessRun.createdAt,
            processIdentifier: processIdentifier,
            updatedAt: new Date().toISOString()
          };

          const mutation = `
            mutation UpdateProcessRun($processRunId: Int!, $name: String, $description: String, $processData: String) {
              updateProcessRun(
                processRunId: $processRunId,
                name: $name,
                description: $description,
                processData: $processData
              ) {
                id
                name
                description
                processType
                status
                createdAt
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: {
              processRunId: this.currentProcessRun.id,
              name: newName,
              description: newDescription,
              processData: JSON.stringify(updatedProcessData)
            }
          });

          if (response && response.data && response.data.data && response.data.data.updateProcessRun) {
            // Update local ProcessRun object
            this.currentProcessRun = response.data.data.updateProcessRun;
            console.log('ProcessRun updated with Process Identifier:', this.currentProcessRun);
          } else {
            console.error('Failed to update ProcessRun:', response);
          }
        } catch (error) {
          console.error('Error updating ProcessRun with Process Identifier:', error);
        }
      },

      onSlideChange() {
        if (this.$refs.slideshowSwiper && this.$refs.slideshowSwiper.swiper) {
          this.currentSlide = this.$refs.slideshowSwiper.swiper.realIndex + 1
          this.$emit('slide-change', this.currentSlide - 1)
          
          // Check for assign-sop step after slide change
          this.$nextTick(() => {
            this.checkAndAutoSubmitAssignSOP(this.currentSlide);
          });
        }
      },

      onReachEnd() {
        // Don't auto-trigger completion, let user click the button
        console.log('Reached completion slide')
      },

      openImageModal(imageSrc) {
        this.modalImageSrc = imageSrc
        this.showImageModal = true
      },

      closeImageModal() {
        this.showImageModal = false
        this.modalImageSrc = ''
      },

      toggleFullscreen() {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen()
          this.isFullscreen = true
        } else {
          document.exitFullscreen()
          this.isFullscreen = false
        }
      },

      openInTCVelik() {
        const url = new URL(window.location.href);

        // Remove the query parameter
        url.searchParams.delete("mode");

        // Get the updated URL string
        const cleanUrl = url.toString();

        window.open(cleanUrl);
      },

      setupKeyboardNavigation() {
        document.addEventListener('keydown', this.handleKeydown)
      },

      setupFullscreenDetection() {
        document.addEventListener('fullscreenchange', this.handleFullscreenChange)
      },

      setupResizeDetection() {
        window.addEventListener('resize', this.handleResize)
      },

      cleanupEventListeners() {
        document.removeEventListener('keydown', this.handleKeydown)
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
        window.removeEventListener('resize', this.handleResize)
      },

      handleKeydown(event) {
        if (!this.$refs.slideshowSwiper || !this.$refs.slideshowSwiper.swiper) return

        const swiper = this.$refs.slideshowSwiper.swiper

        switch (event.key) {
          //case 'ArrowLeft':
          //  swiper.slidePrev()
          //  break
          //case 'ArrowRight':
          //  swiper.slideNext()
          //  break
          case 'f':
          case 'F':
            this.toggleFullscreen()
            break
        }
      },

      handleFullscreenChange() {
        this.isFullscreen = !!document.fullscreenElement
        // Recalculate sizing when fullscreen changes
        this.$nextTick(() => {
          this.adjustForZoom()
        })
      },

      handleResize() {
        // Debounce resize events to avoid excessive recalculations
        clearTimeout(this.resizeTimeout)
        this.resizeTimeout = setTimeout(() => {
          this.adjustForZoom()
        }, 100)
      },

      async markAsCompleted() {
        // If we're in a ProcessRun context, track step completion
        if (this.currentProcessRun && this.currentProcessRun.id) {
          await this.completeAllStepsInProcessRun();
        }
        
        this.$emit('presentation-complete')
      },

      async stealThisSOP() {
        // Get the HTML content from the onboarding data
        const htmlContent = this.generateSOPHtmlContent();
        
        const result = await TCVelikExportImportService.exportToTCVelik({
          title: this.onboardingData.title || 'Untitled SOP',
          description: this.onboardingData.description || '',
          htmlContent: htmlContent,
          progressCallback: (progress) => {
            this.exportProgressActive = progress.active;
            this.exportCurrentStep = progress.currentStep;
            this.exportTotalSteps = progress.totalSteps;
          }
        });

        if (result.success) {
          this.$vs.notify({
            title: this.$t('SOPExported'),
            text: this.$t('SOPExportedSuccessfully'),
            color: 'success',
            iconPack: 'feather',
            position: 'top-center',
            icon: 'icon-check-circle',
          });
        } else {
          console.error('Export failed:', result.error);
          this.$vs.notify({
            title: this.$t('ExportFailed'),
            text: result.error,
            color: 'danger',
            iconPack: 'feather',
            position: 'top-center',
            icon: 'icon-alert-circle',
          });
        }
      },

      generateSOPHtmlContent() {
        // Convert onboardingData.steps into HTML format similar to the editor content
        let html = '';
        
        if (this.onboardingData.steps && Array.isArray(this.onboardingData.steps)) {
          this.onboardingData.steps.forEach((step, index) => {
            const subtype = step.subtype || 'informational';
            const metadata = step.metadata ? JSON.stringify(step.metadata) : '{}';
            
            // Build the step HTML structure
            let imagesHtml = '';
            if (step.images && step.images.length) {
              imagesHtml = step.images.map(img => `<img src="${img.src}" alt="${img.alt || step.title}" />`).join('');
            }
            
            html += `<div data-type="draggable-item" data-subtype="${subtype}">
              <p><strong>${step.title || 'Step ' + (index + 1)}</strong></p>
              <p>${step.description || ''}${imagesHtml}</p>
              <p>${metadata}</p>
            </div>`;
          });
        }
        
        return html;
      },

      openCodeFullscreen(code) {
        this.$emit('open-code-fullscreencomplete',code)
      },

      async openFormPopup(formId) {
        this.currentFormId = formId;
        this.showFormModal = true;
        this.currentFormData = null;
        this.formSubmissionData = {};
        
        try {
          const query = `
            query FormById($id: Int!) {
              formById(id: $id) {
                id
                title
                formMetadata
                createdAt
                updatedAt
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query,
            variables: { id: parseInt(formId) }
          });

          if (response && response.data && response.data.data && response.data.data.formById) {
            const form = response.data.data.formById;
            this.currentFormData = {
              id: form.id,
              title: form.title,
              summary: '',
              fields: []
            };
            
            // Parse form metadata if it exists
            if (form.formMetadata) {
              try {
                const metadata = JSON.parse(form.formMetadata);
                if (metadata.summary) {
                  this.currentFormData.summary = metadata.summary;
                }
                if (metadata.fields && Array.isArray(metadata.fields)) {
                  this.currentFormData.fields = metadata.fields;
                }
              } catch (error) {
                console.error('Error parsing form metadata:', error);
              }
            }
          } else {
            this.$vs.notify({
              title: 'Error',
              text: 'Form not found',
              color: 'danger'
            });
            this.closeFormModal();
          }
        } catch (error) {
          console.error('Error loading form:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to load form',
            color: 'danger'
          });
          this.closeFormModal();
        }
      },

      async submitForm() {
        debugger;
        if (!this.isFormValid) {
          this.$vs.notify({
            title: 'Validation Error',
            text: 'Please fill in all required fields',
            color: 'danger'
          });
          return;
        }

        try {
          // Extract Process Identifier from form submission
          const processIdentifier = this.extractProcessIdentifier();
          
          // Submit form to backend with ProcessRun context if available
          let mutation, variables;
          
          if (this.currentProcessRun && this.currentProcessRun.id) {
            // Use ProcessRun context
            mutation = `
              mutation SubmitFormInProcessRun($formId: Int!, $submissionData: String!, $processRunId: Int!, $contextData: String) {
                submitFormInProcessRun(
                  formId: $formId, 
                  submissionData: $submissionData, 
                  processRunId: $processRunId, 
                  contextData: $contextData
                ) {
                  id
                  formId
                  submissionData
                  submittedAt
                  submittedBy
                }
              }
            `;
            
            const contextData = {
              onboardingId: this.onboardingData.id,
              onboardingTitle: this.onboardingData.title,
              stepIndex: this.currentSlide - 2, // Adjust for title slide
              submittedAt: new Date().toISOString(),
              processIdentifier: processIdentifier // Add Process Identifier to context
            };
            
            variables = {
              formId: parseInt(this.currentFormId),
              submissionData: JSON.stringify(this.formSubmissionData),
              processRunId: this.currentProcessRun.id,
              contextData: JSON.stringify(contextData)
            };
          } else {
            // Fallback to regular form submission
            mutation = `
              mutation SubmitForm($formId: Int!, $submissionData: String!) {
                submitForm(formId: $formId, submissionData: $submissionData) {
                  id
                  formId
                  submissionData
                  submittedAt
                  submittedBy
                }
              }
            `;
            
            variables = {
              formId: parseInt(this.currentFormId),
              submissionData: JSON.stringify(this.formSubmissionData)
            };
          }

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: variables
          });

          const submissionResult = response && response.data && response.data.data && 
            (response.data.data.submitForm || response.data.data.submitFormInProcessRun);
          
          if (submissionResult) {
            // Mark form as completed
            this.completedForms.add(this.currentFormId);
            
            // Store Process Identifier for future use
            if (processIdentifier) {
              this.currentProcessIdentifier = processIdentifier;
            }
            
            // Update ProcessRun if Process Identifier was provided
            if (processIdentifier && this.currentProcessRun && this.currentProcessRun.id) {
              await this.updateProcessRunWithIdentifier(processIdentifier);
            }

            // Update ProcessRun context with form submission
            if (this.currentProcessRun && this.currentProcessRun.id) {
              await this.updateProcessRunWithFormSubmission(this.currentFormId, submissionResult);
            }
            
            this.$vs.notify({
              title: 'Success',
              text: 'Form submitted successfully!',
              color: 'success'
            });

            this.closeFormModal();
          } else {
            throw new Error('Failed to submit form');
          }
        } catch (error) {
          console.error('Error submitting form:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to submit form. Please try again.',
            color: 'danger'
          });
        }
      },

      closeFormModal() {
        this.showFormModal = false;
        this.currentFormId = null;
        this.currentFormData = null;
        this.formSubmissionData = {};
      },

      async openAssignSOPModal(sopId) {
        this.showAssignSOPModal = true;
        this.currentAssignSOPData = null;
        this.assignSOPFormData = {
          userId: '',
          dueDate: this.getDefaultDueDate(),
          selectedSOP: null,
          allowUserToChangeValues: false,
          dateType: 'absolute',
          relativeDays: 5,
          relativeEvent: 'sop-completion'
        };
        
        // Load SOP details and users in parallel
        await Promise.all([
          this.loadSOPDetails(sopId),
          this.loadAvailableUsers(),
          this.loadAvailableSOPs()
        ]);
      },

      async loadSOPDetails(sopId) {
        try {
          const query = `
            query GetOnboarding($id: Int!) {
              onboardings(where: { id: { eq: $id } }) {
                id
                name
                description
                created
                lastModified
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query,
            variables: { id: parseInt(sopId) }
          });

          if (response && response.data && response.data.data && response.data.data.onboardings && response.data.data.onboardings.length > 0) {
            this.currentAssignSOPData = response.data.data.onboardings[0];
          } else {
            this.$vs.notify({
              title: 'Error',
              text: 'SOP not found',
              color: 'danger'
            });
            this.closeAssignSOPModal();
          }
        } catch (error) {
          console.error('Error loading SOP details:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to load SOP details',
            color: 'danger'
          });
          this.closeAssignSOPModal();
        }
      },

      async loadSOPDetailsForAssignment(sopId) {
        try {
          const query = `
            query GetOnboarding($id: Int!) {
              onboardings(where: { id: { eq: $id } }) {
                id
                name
                description
                created
                lastModified
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query,
            variables: { id: parseInt(sopId) }
          });

          if (response && response.data && response.data.data && response.data.data.onboardings && response.data.data.onboardings.length > 0) {
            return response.data.data.onboardings[0];
          } else {
            console.error('SOP not found for ID:', sopId);
            return null;
          }
        } catch (error) {
          console.error('Error loading SOP details for assignment:', error);
          return null;
        }
      },

      async loadAvailableUsers() {
        try {
          const query = `
            query Users($skip: Int, $take: Int, $search: String, $sortType: String, $sortOrder: String!) {
              users(skip: $skip, take: $take, search: $search, sortType: $sortType, sortOrder: $sortOrder) {
                totalCount
                result {
                  id
                  firstName
                  lastName
                  email
                  userName
                }
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query,
            variables: {
              skip: 0,
              take: 1000, // Get all users for the dropdown
              search: '',
              sortType: 'lld',
              sortOrder: 'desc'
            }
          });

          console.log('Users response:', response);
          
          if (response && response.data && response.data.data && response.data.data.users && response.data.data.users.result) {
            this.availableUsers = response.data.data.users.result;
            console.log('Available users:', this.availableUsers);
          } else {
            console.log('No users found in response');
            this.availableUsers = [];
          }
        } catch (error) {
          console.error('Error loading users:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to load users',
            color: 'danger'
          });
        }
      },

      getDefaultDueDate() {
        const date = new Date();
        date.setDate(date.getDate() + 7); // 1 week from now
        return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      },

      isAssignmentSubmitted(sopId) {
        // Check if any assignment for this SOP has been submitted in this session
        for (const assignmentKey of this.submittedAssignments) {
          if (assignmentKey.startsWith(`${sopId}-`)) {
            return true;
          }
        }
        return false;
      },

      async checkAndAutoSubmitAssignSOP(slideIndex) {
        // Add a small delay to ensure the slide transition is complete
        await this.$nextTick();
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay to ensure slide is fully loaded
        
        // Get the current step (slideIndex is 1-based, steps are 0-based)
        const stepIndex = slideIndex - 2; // Convert to 0-based index
        if (stepIndex < 0 || stepIndex >= this.onboardingData.steps.length) {
          return;
        }

        const step = this.onboardingData.steps[stepIndex];
        
        // Check if this is an assign-sop step
        if (step.subtype === 'assign-sop' && step.metadata && step.metadata.sopId) {          
          // If assignment already submitted, don't do anything
          if (this.isAssignmentSubmitted(step.metadata.sopId)) {
            return;
          }

          // If user cannot change values, auto-submit the assignment
          if (!step.metadata.allowUserToChangeValues) {
            await this.autoSubmitAssignSOP(step.metadata);
          }
        }
      },

      async autoSubmitAssignSOP(metadata) {
        try {
          // Get current user (you might need to adjust this based on your auth system)
          const currentUser = this.getCurrentUser();
          if (!currentUser) {
            console.error('No current user found for auto-assignment');
            return;
          }

          // Load SOP details to get the name
          const sopDetails = await this.loadSOPDetailsForAssignment(metadata.sopId);
          if (!sopDetails) {
            return;
          }

          // Calculate due date based on metadata
          let dueDate;
          if (metadata.dateType === 'relative') {
            // Calculate relative date based on the event type
            const relativeDays = parseInt(metadata.relativeDays) || 5;
            console.log('Relative date calculation:', {
              relativeDays: relativeDays,
              relativeEvent: metadata.relativeEvent,
              type: typeof metadata.relativeDays,
              originalValue: metadata.relativeDays
            });
            
            if (metadata.relativeEvent === 'sop-completion') {
              // SOP completion: current date + relative days
              dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + relativeDays);
            } else if (metadata.relativeEvent && metadata.relativeEvent.startsWith('form-')) {
              // Form field completion: get form field value + relative days
              dueDate = await this.calculateDueDateFromFormField(metadata.relativeEvent, relativeDays);
            } else {
              // Fallback to current date + relative days
              dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + relativeDays);
            }
            
            console.log('Calculated due date:', dueDate.toISOString());
          } else {
            // Use absolute date or default to 1 week from now
            if (metadata.dueDate) {
              dueDate = new Date(metadata.dueDate);
            } else {
              dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + 7);
            }
          }

          // Extract Process Identifier for assignment title
          const processIdentifier = this.extractProcessIdentifier();
          const processId = this.currentProcessRun?.id || 'NoProcess';
          const titlePrefix = processIdentifier ? `[${processIdentifier}] #${processId} ` : `#${processId} `;

          // Use ProcessRun context if available
          let mutation, variables;
          
          if (this.currentProcessRun && this.currentProcessRun.id) {
            // Use ProcessRun context
            mutation = `
              mutation AssignScheduleInProcessRun($assignScheduleDTO: AssignScheduleDTOInput!, $processRunId: Int!, $contextData: String) {
                assignScheduleInProcessRun(
                  assignScheduleDTO: $assignScheduleDTO,
                  processRunId: $processRunId,
                  contextData: $contextData
                )
              }
            `;
            
            const contextData = {
              onboardingId: this.onboardingData.id,
              onboardingTitle: this.onboardingData.title,
              stepIndex: this.currentSlide - 2,
              assignedAt: new Date().toISOString(),
              allowUserToChangeValues: metadata.allowUserToChangeValues || false,
              processRunId: this.currentProcessRun.id,
              processRunName: this.currentProcessRun.name,
              processRunType: this.currentProcessRun.processType,
              autoAssigned: true,
              processIdentifier: processIdentifier // Add Process Identifier to context
            };
            
            variables = {
              assignScheduleDTO: {
                onboardingId: parseInt(metadata.sopId),
                userId: currentUser.id,
                dateDue: dueDate.toISOString(),
                type: 2, // COMPLETE_SOP type
                cadence: -1, // Custom date (use dateDue)
                customTitle: `${titlePrefix}${sopDetails.name}`
              },
              processRunId: this.currentProcessRun.id,
              contextData: JSON.stringify(contextData)
            };
          } else {
            // Fallback to regular assignment
            mutation = `
              mutation AssignSchedule($onboardingId: Int!, $userId: String!, $dateDue: DateTime!, $type: Int!, $cadence: Int!, $customTitle: String) {
                assignSchedule(assignScheduleDTO: {
                  onboardingId: $onboardingId,
                  userId: $userId,
                  dateDue: $dateDue,
                  type: $type,
                  cadence: $cadence,
                  customTitle: $customTitle
                })
              }
            `;
            
            variables = {
              onboardingId: parseInt(metadata.sopId),
              userId: currentUser.id,
              dateDue: dueDate.toISOString(),
              type: 2, // COMPLETE_SOP type
              cadence: -1, // Custom date (use dateDue)
              customTitle: `${titlePrefix}${sopDetails.name}`
            };
          }

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: variables
          });

          const assignmentResult = response && response.data && response.data.data && 
            (response.data.data.assignSchedule || response.data.data.assignScheduleInProcessRun);
          
          if (assignmentResult) {
            // Track this assignment to prevent duplicate submissions
            const assignmentKey = `${metadata.sopId}-${currentUser.id}`;
            this.submittedAssignments.add(assignmentKey);
                        
            this.$vs.notify({
              title: 'Assignment Created',
              text: 'SOP assignment has been automatically created',
              color: 'success'
            });
          } else {

          }
        } catch (error) {
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to create automatic assignment',
            color: 'danger'
          });
        }
      },

      getCurrentUser() {
        try {
          // Try to get user from Vuex store first
          if (this.$store.state.currentUser) {
            return {
              id: this.$store.state.currentUser.id,
              email: this.$store.state.currentUser.email
            };
          }

          // Fallback to localStorage
          const userInfo = localStorage.getItem('fullUserInfo');
          if (userInfo) {
            const userData = JSON.parse(userInfo);
            if (userData.User) {
              return {
                id: userData.User.id,
                email: userData.User.email
              };
            }
          }

          console.error('No current user found');
          return null;
        } catch (error) {
          console.error('Error getting current user:', error);
          return null;
        }
      },

      async calculateDueDateFromFormField(relativeEvent, relativeDays) {
        try {
          // Parse the relative event to extract form ID and field ID
          // Format: "form-{formId}-{fieldId}"
          const parts = relativeEvent.split('-');
          if (parts.length >= 3) {
            const formId = parts[1];
            const fieldId = parts[2];
            
            console.log('Calculating due date from form field:', { formId, fieldId, relativeDays });
            
            // Get the form field value from the current ProcessRun
            const formFieldValue = await this.getFormFieldValueFromProcessRun(parseInt(formId), fieldId);
            
            if (formFieldValue) {
              // Parse the form field value as a date
              const baseDate = new Date(formFieldValue);
              
              if (!isNaN(baseDate.getTime())) {
                // Add the relative days to the form field date
                const dueDate = new Date(baseDate);
                dueDate.setDate(dueDate.getDate() + relativeDays);
                
                console.log('Form field due date calculation:', {
                  formFieldValue,
                  baseDate: baseDate.toISOString(),
                  relativeDays,
                  dueDate: dueDate.toISOString()
                });
                
                return dueDate;
              } else {
                console.warn('Form field value is not a valid date:', formFieldValue);
              }
            } else {
              console.warn('Form field value not found for form', formId, 'field', fieldId);
            }
            
            // Fallback to current date + relative days if form field value is not available
            const dueDate = new Date();
            dueDate.setDate(dueDate.getDate() + relativeDays);
            
            console.log('Form field due date calculation (fallback):', dueDate.toISOString());
            return dueDate;
          }
        } catch (error) {
          console.error('Error calculating due date from form field:', error);
        }
        
        // Fallback to current date + relative days
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + relativeDays);
        return dueDate;
      },

      async submitAssignSOP() {

        if (!this.isAssignSOPFormValid) {
          this.$vs.notify({
            title: 'Validation Error',
            text: `Please fill in all required fields. SOP: ${this.assignSOPFormData.selectedSOP ? 'Selected' : 'Not selected'}, UserId: ${this.assignSOPFormData.userId}, DateType: ${this.assignSOPFormData.dateType}`,
            color: 'danger'
          });
          return;
        }

        // Check if this assignment has already been submitted in this session
        const assignmentKey = `${this.assignSOPFormData.selectedSOP.id}-${this.assignSOPFormData.userId}`;
        if (this.submittedAssignments.has(assignmentKey)) {
          this.$vs.notify({
            title: 'Already Submitted',
            text: 'This assignment has already been submitted in this session',
            color: 'warning'
          });
          return;
        }

        try {
          // Calculate due date based on form data
          let dueDate;
          if (this.assignSOPFormData.dateType === 'relative') {
            // Calculate relative date based on the event type
            const relativeDays = parseInt(this.assignSOPFormData.relativeDays) || 5;
            console.log('Form relative date calculation:', {
              relativeDays: relativeDays,
              relativeEvent: this.assignSOPFormData.relativeEvent,
              type: typeof this.assignSOPFormData.relativeDays,
              originalValue: this.assignSOPFormData.relativeDays
            });
            
            if (this.assignSOPFormData.relativeEvent === 'sop-completion') {
              // SOP completion: current date + relative days
              dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + relativeDays);
            } else if (this.assignSOPFormData.relativeEvent && this.assignSOPFormData.relativeEvent.startsWith('form-')) {
              // Form field completion: get form field value + relative days
              dueDate = await this.calculateDueDateFromFormField(this.assignSOPFormData.relativeEvent, relativeDays);
            } else {
              // Fallback to current date + relative days
              dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + relativeDays);
            }
            
            console.log('Form calculated due date:', dueDate.toISOString());
          } else {
            // Use absolute date
            dueDate = new Date(this.assignSOPFormData.dueDate);
          }

          // Extract Process Identifier for assignment title
          const processIdentifier = this.extractProcessIdentifier();
          const processId = this.currentProcessRun?.id || 'NoProcess';
          const titlePrefix = processIdentifier ? `[${processIdentifier}] #${processId} ` : `#${processId} `;
          
          // Use ProcessRun context if available
          let mutation, variables;
          
          if (this.currentProcessRun && this.currentProcessRun.id) {
            // Use ProcessRun context
            mutation = `
              mutation AssignScheduleInProcessRun($assignScheduleDTO: AssignScheduleDTOInput!, $processRunId: Int!, $contextData: String) {
                assignScheduleInProcessRun(
                  assignScheduleDTO: $assignScheduleDTO,
                  processRunId: $processRunId,
                  contextData: $contextData
                )
              }
            `;
            
            const contextData = {
              onboardingId: this.onboardingData.id,
              onboardingTitle: this.onboardingData.title,
              stepIndex: this.currentSlide - 2, // Adjust for title slide
              assignedAt: new Date().toISOString(),
              allowUserToChangeValues: this.assignSOPFormData.allowUserToChangeValues,
              processRunId: this.currentProcessRun.id, // 🔑 Store ProcessRun ID in metadata
              processRunName: this.currentProcessRun.name,
              processRunType: this.currentProcessRun.processType,
              processIdentifier: processIdentifier // Add Process Identifier to context
            };
            
            variables = {
              assignScheduleDTO: {
                onboardingId: parseInt(this.assignSOPFormData.selectedSOP.id),
                userId: this.assignSOPFormData.userId,
                dateDue: dueDate.toISOString(),
                type: 2, // COMPLETE_SOP type
                cadence: -1, // Custom date (use dateDue)
                customTitle: `${titlePrefix}${this.assignSOPFormData.selectedSOP.name}`
              },
              processRunId: this.currentProcessRun.id,
              contextData: JSON.stringify(contextData)
            };
          } else {
            // Fallback to regular assignment
            mutation = `
              mutation AssignSchedule($onboardingId: Int!, $userId: String!, $dateDue: DateTime!, $type: Int!, $cadence: Int!, $customTitle: String) {
                assignSchedule(assignScheduleDTO: {
                  onboardingId: $onboardingId,
                  userId: $userId,
                  dateDue: $dateDue,
                  type: $type,
                  cadence: $cadence,
                  customTitle: $customTitle
                })
              }
            `;
            
            variables = {
              onboardingId: parseInt(this.assignSOPFormData.selectedSOP.id),
              userId: this.assignSOPFormData.userId,
              dateDue: new Date(this.assignSOPFormData.dueDate).toISOString(),
              type: 2, // COMPLETE_SOP type
              cadence: -1, // Custom date (use dateDue)
              customTitle: `${titlePrefix}${this.assignSOPFormData.selectedSOP.name}`
            };
          }

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: variables
          });

          if (response && response.data && response.data.data && response.data.data.assignSchedule) {
            // Track this assignment to prevent duplicate submissions
            this.submittedAssignments.add(assignmentKey);
            
            this.$vs.notify({
              title: 'Success',
              text: 'SOP assignment created successfully!',
              color: 'success'
            });
            this.closeAssignSOPModal();
          } else {
            throw new Error('Failed to create assignment');
          }
        } catch (error) {
          console.error('Error creating SOP assignment:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to create SOP assignment',
            color: 'danger'
          });
        }
      },

      async submitAssignSOPFromStep(sopId) {
        // Validate form based on date type
        const isValidAbsolute = this.assignSOPFormData.dateType === 'absolute' && this.assignSOPFormData.dueDate;
        const isValidRelative = this.assignSOPFormData.dateType === 'relative' && this.assignSOPFormData.relativeDays;
        
        if (!this.assignSOPFormData.userId || (!isValidAbsolute && !isValidRelative)) {
          this.$vs.notify({
            title: 'Validation Error',
            text: 'Please fill in all required fields',
            color: 'danger'
          });
          return;
        }

        // Check if this assignment has already been submitted in this session
        const assignmentKey = `${sopId}-${this.assignSOPFormData.userId}`;
        if (this.submittedAssignments.has(assignmentKey)) {
          this.$vs.notify({
            title: 'Already Submitted',
            text: 'This assignment has already been submitted in this session',
            color: 'warning'
          });
          return;
        }

        try {
          // Load SOP details to get the name
          const sopDetails = await this.loadSOPDetailsForAssignment(sopId);
          if (!sopDetails) {
            console.error('Failed to load SOP details for assignment');
            this.$vs.notify({
              title: 'Error',
              text: 'Failed to load SOP details',
              color: 'danger'
            });
            return;
          }

          // Calculate due date based on form data
          let dueDate;
          if (this.assignSOPFormData.dateType === 'relative') {
            // Calculate relative date based on the event type
            const relativeDays = parseInt(this.assignSOPFormData.relativeDays) || 5;
            console.log('Step form relative date calculation:', {
              relativeDays: relativeDays,
              relativeEvent: this.assignSOPFormData.relativeEvent,
              type: typeof this.assignSOPFormData.relativeDays,
              originalValue: this.assignSOPFormData.relativeDays
            });
            
            if (this.assignSOPFormData.relativeEvent === 'sop-completion') {
              // SOP completion: current date + relative days
              dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + relativeDays);
            } else if (this.assignSOPFormData.relativeEvent && this.assignSOPFormData.relativeEvent.startsWith('form-')) {
              // Form field completion: get form field value + relative days
              dueDate = await this.calculateDueDateFromFormField(this.assignSOPFormData.relativeEvent, relativeDays);
            } else {
              // Fallback to current date + relative days
              dueDate = new Date();
              dueDate.setDate(dueDate.getDate() + relativeDays);
            }
            
            console.log('Step form calculated due date:', dueDate.toISOString());
          } else {
            // Use absolute date
            dueDate = new Date(this.assignSOPFormData.dueDate);
          }

          // Extract Process Identifier for assignment title
          const processIdentifier = this.extractProcessIdentifier();
          const processId = this.currentProcessRun?.id || 'NoProcess';
          const titlePrefix = processIdentifier ? `[${processIdentifier}] #${processId} ` : `#${processId} `;
          
          debugger;
          // Use ProcessRun context if available
          
          let mutation, variables;
          
          if (this.currentProcessRun && this.currentProcessRun.id) {
            // Use ProcessRun context
            mutation = `
              mutation AssignScheduleInProcessRun($assignScheduleDTO: AssignScheduleDTOInput!, $processRunId: Int!, $contextData: String) {
                assignScheduleInProcessRun(
                  assignScheduleDTO: $assignScheduleDTO,
                  processRunId: $processRunId,
                  contextData: $contextData
                )
              }
            `;
            
            const contextData = {
              onboardingId: this.onboardingData.id,
              onboardingTitle: this.onboardingData.title,
              stepIndex: this.currentSlide - 2,
              assignedAt: new Date().toISOString(),
              allowUserToChangeValues: true, // User can change values in this flow
              processRunId: this.currentProcessRun.id,
              processRunName: this.currentProcessRun.name,
              processRunType: this.currentProcessRun.processType,
              fromStep: true,
              processIdentifier: processIdentifier // Add Process Identifier to context
            };
            
            variables = {
              assignScheduleDTO: {
                onboardingId: parseInt(sopId),
                userId: this.assignSOPFormData.userId,
                dateDue: dueDate.toISOString(),
                type: 2, // COMPLETE_SOP type
                cadence: -1, // Custom date (use dateDue)
                customTitle: `${titlePrefix}${sopDetails.name}`
              },
              processRunId: this.currentProcessRun.id,
              contextData: JSON.stringify(contextData)
            };
          } else {
            // Fallback to regular assignment
            mutation = `
              mutation AssignSchedule($onboardingId: Int!, $userId: String!, $dateDue: DateTime!, $type: Int!, $cadence: Int!, $customTitle: String) {
                assignSchedule(assignScheduleDTO: {
                  onboardingId: $onboardingId,
                  userId: $userId,
                  dateDue: $dateDue,
                  type: $type,
                  cadence: $cadence,
                  customTitle: $customTitle
                })
              }
            `;
            
            variables = {
              onboardingId: parseInt(sopId),
              userId: this.assignSOPFormData.userId,
              dateDue: dueDate.toISOString(),
              type: 2, // COMPLETE_SOP type
              cadence: -1, // Custom date (use dateDue)
              customTitle: `${titlePrefix}${sopDetails.name}`
            };
          }

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: variables
          });

          const assignmentResult = response && response.data && response.data.data && 
            (response.data.data.assignSchedule || response.data.data.assignScheduleInProcessRun);
          
          if (assignmentResult) {
            // Track this assignment to prevent duplicate submissions
            this.submittedAssignments.add(assignmentKey);
            
            this.$vs.notify({
              title: 'Success',
              text: 'SOP assignment updated successfully',
              color: 'success'
            });
          }
        } catch (error) {
          console.error('Error updating assignment:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to update assignment',
            color: 'danger'
          });
        }
      },

      closeAssignSOPModal() {
        this.showAssignSOPModal = false;
        this.currentAssignSOPData = null;
        this.assignSOPFormData = {
          userId: '',
          dueDate: null,
          selectedSOP: null,
          allowUserToChangeValues: false
        };
        this.availableUsers = [];
      },

      async loadAvailableSOPs() {
        try {
          const query = `
            query GetOnboardings {
              onboardings {
                id
                name
                description
                created
                lastModified
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query
          });

          if (response && response.data && response.data.data && response.data.data.onboardings) {
            this.availableSOPs = response.data.data.onboardings;
          } else {
            this.availableSOPs = [];
          }
        } catch (error) {
          console.error('Error loading available SOPs:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to load available SOPs',
            color: 'danger'
          });
          this.availableSOPs = [];
        }
      },

      openSOPSelector() {
        this.showSOPSelector = true;
      },

      closeSOPSelector() {
        this.showSOPSelector = false;
      },

      selectSOP(sop) {
        this.assignSOPFormData.selectedSOP = sop;
        this.closeSOPSelector();
      },

      onUserSelected(value) {
        console.log('User selected:', value);
        this.assignSOPFormData.userId = value;
      },

      startOver() {
        if (this.$refs.slideshowSwiper && this.$refs.slideshowSwiper.swiper) {
          this.$refs.slideshowSwiper.swiper.slideTo(0)
        }
      },

      adjustForZoom() {
        // Check if body has zoom applied
        const bodyZoom = document.body.style.zoom
        const isFullscreen = !!document.fullscreenElement

        // Get the container element
        const container = this.$el
        if (container) {
          let actualWidth, actualHeight

          if (bodyZoom && bodyZoom !== '') {
            const zoomFactor = parseFloat(bodyZoom) / 100 // Convert percentage to decimal

            // Calculate the actual viewport dimensions accounting for zoom
            actualWidth = window.innerWidth / zoomFactor
            actualHeight = window.innerHeight / zoomFactor
          } else {
            // No zoom applied, use viewport units but ensure full coverage
            actualWidth = window.innerWidth
            actualHeight = window.innerHeight
          }

          // Set the container dimensions to cover the entire screen
          container.style.width = `${actualWidth}px`
          container.style.height = `${actualHeight}px`
          container.style.position = 'fixed'
          container.style.top = '0'
          container.style.left = '0'
          container.style.zIndex = '9999'

          // Update swiper if available
          if (this.$refs.slideshowSwiper && this.$refs.slideshowSwiper.swiper) {
            this.$refs.slideshowSwiper.swiper.update()
          }

          console.log('Adjusted slideshow for zoom/fullscreen:', {
            zoomFactor: bodyZoom ? parseFloat(bodyZoom) / 100 : 1,
            isFullscreen,
            actualWidth,
            actualHeight,
            originalWidth: window.innerWidth,
            originalHeight: window.innerHeight
          })
        }
      },

      // Complete all steps in the current ProcessRun
      async completeAllStepsInProcessRun() {
        try {
          const currentUser = this.getCurrentUser();
          if (!currentUser) {
            console.warn('No current user found for step completion');
            return;
          }

          // Extract Process Identifier for context
          const processIdentifier = this.extractProcessIdentifier();

          // Complete each step in the onboarding
          for (let i = 0; i < this.onboardingData.steps.length; i++) {
            const step = this.onboardingData.steps[i];
            const stepId = step.id || `step-${i}`;

            const contextData = {
              onboardingId: this.onboardingData.id,
              onboardingTitle: this.onboardingData.title,
              stepIndex: i,
              stepTitle: step.title,
              stepSubtype: step.subtype,
              completedAt: new Date().toISOString(),
              completedBy: currentUser.email,
              processIdentifier: processIdentifier,
              processRunId: this.currentProcessRun.id,
              processRunName: this.currentProcessRun.name,
              processRunType: this.currentProcessRun.processType,
              completionType: 'full_onboarding_completion'
            };

            debugger;

            const mutation = `
              mutation CompleteStepInProcessRun($processRunId: Int!, $onboardingId: Int!, $stepId: String!, $contextData: String) {
                completeStepInProcessRun(
                  processRunId: $processRunId,
                  onboardingId: $onboardingId,
                  stepId: $stepId,
                  contextData: $contextData
                ) {
                  id
                  processRunId
                  onboardingId
                  stepId
                  completedBy
                  completedAt
                }
              }
            `;

            console.log('Completing step with variables:', {
              processRunId: this.currentProcessRun.id,
              onboardingId: this.onboardingData.id,
              stepId: stepId,
              onboardingData: this.onboardingData
            });

            // Check if onboardingId is available
            if (!this.onboardingData.id) {
              console.error('onboardingData.id is missing:', this.onboardingData);
              this.$vs.notify({
                title: 'Error',
                text: 'Onboarding ID is missing. Cannot track step completion.',
                color: 'danger'
              });
              return;
            }

            const response = await TCVelikClient.PostRelative('/api/graphql', {
              query: mutation,
              variables: {
                processRunId: parseInt(this.currentProcessRun.id),
                onboardingId: parseInt(this.onboardingData.id),
                stepId: stepId,
                contextData: JSON.stringify(contextData)
              }
            });

            if (response && response.data && response.data.data && response.data.data.completeStepInProcessRun) {
              console.log('Step completed in ProcessRun:', stepId);
              
              // Update ProcessRun with completed SOP
              await this.updateProcessRunWithCompletedSOP(step, i);
              
              // Check if this is an assign-sop step with relative date
              if (step.subtype === 'assign-sop' && step.metadata && step.metadata.dateType === 'relative' && step.metadata.relativeEvent === 'sop-completion') {
                console.log('Found relative date assign-sop step, creating assignment...');
                await this.createAssignmentOnSOPCompletion(step.metadata);
              }
            }
          }

          console.log('All steps completed in ProcessRun:', this.currentProcessRun.id);
        } catch (error) {
          console.error('Error completing steps in ProcessRun:', error);
          this.$vs.notify({
            title: 'Warning',
            text: 'Failed to track step completion in ProcessRun',
            color: 'warning'
          });
        }
      },

      async createAssignmentOnSOPCompletion(stepMetadata) {
        try {
          console.log('Creating assignment on SOP completion:', stepMetadata);
          
          // Calculate due date based on relative days
          const relativeDays = parseInt(stepMetadata.relativeDays) || 5;
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + relativeDays);
          
          console.log('Calculated due date:', dueDate.toISOString());
          
          // Get current user for assignment
          const currentUser = this.getCurrentUser();
          if (!currentUser) {
            console.error('No current user found for assignment creation');
            return;
          }
          
          // Load SOP details
          const sopDetails = await this.loadSOPDetailsForAssignment(stepMetadata.sopId);
          if (!sopDetails) {
            console.error('Failed to load SOP details for assignment');
            return;
          }
          
          // Extract Process Identifier for assignment title
          const processIdentifier = this.extractProcessIdentifier();
          const processId = this.currentProcessRun?.id || 'NoProcess';
          const titlePrefix = processIdentifier ? `[${processIdentifier}] #${processId} ` : `#${processId} `;
          
          const mutation = `
            mutation AssignScheduleInProcessRun($assignScheduleDTO: AssignScheduleDTOInput!, $processRunId: Int!, $contextData: String) {
              assignScheduleInProcessRun(assignScheduleDTO: $assignScheduleDTO, processRunId: $processRunId, contextData: $contextData)
            }
          `;
          
          const assignScheduleDTO = {
            onboardingId: parseInt(stepMetadata.sopId),
            userId: stepMetadata.userId || currentUser.id,
            dateDue: dueDate.toISOString(),
            cadence: relativeDays,
            type: 0, // REVIEW type
            customTitle: `${titlePrefix}${sopDetails.name}`
          };
          
          const contextData = JSON.stringify({
            assignedFrom: 'sop_completion',
            sourceOnboardingId: this.onboardingData.id,
            sourceOnboardingTitle: this.onboardingData.title,
            assignedAt: new Date().toISOString(),
            assignedBy: currentUser.email,
            processIdentifier: processIdentifier,
            relativeDays: relativeDays,
            originalStepMetadata: stepMetadata
          });
          
          console.log('Assignment DTO:', assignScheduleDTO);
          console.log('ProcessRun ID:', this.currentProcessRun.id);
          console.log('Context Data:', contextData);
          console.log('Mutation:', mutation);
          
          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: {
              assignScheduleDTO: assignScheduleDTO,
              processRunId: parseInt(this.currentProcessRun.id),
              contextData: contextData
            }
          });
          
          console.log('GraphQL Response:', response);
          
          if (response && response.data && response.data.data && response.data.data.assignScheduleInProcessRun) {
            console.log('Assignment created successfully on SOP completion');
            
            this.$vs.notify({
              title: 'Assignment Created',
              text: `Next step "${sopDetails.name}" assigned successfully`,
              color: 'success'
            });
          } else {
            console.error('Failed to create assignment:', response);
          }
        } catch (error) {
          console.error('Error creating assignment on SOP completion:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to create automatic assignment',
            color: 'danger'
          });
        }
      },

      // Complete individual step in ProcessRun (for future use)
      async completeStepInProcessRun(stepIndex) {
        try {
          if (!this.currentProcessRun || !this.currentProcessRun.id) {
            return; // Not in ProcessRun context
          }

          const currentUser = this.getCurrentUser();
          if (!currentUser) {
            console.warn('No current user found for step completion');
            return;
          }

          const step = this.onboardingData.steps[stepIndex];
          const stepId = step.id || `step-${stepIndex}`;

          const contextData = {
            onboardingId: this.onboardingData.id,
            onboardingTitle: this.onboardingData.title,
            stepIndex: stepIndex,
            stepTitle: step.title,
            stepSubtype: step.subtype,
            completedAt: new Date().toISOString(),
            completedBy: currentUser.email,
            processIdentifier: this.extractProcessIdentifier(),
            processRunId: this.currentProcessRun.id,
            processRunName: this.currentProcessRun.name,
            processRunType: this.currentProcessRun.processType,
            completionType: 'individual_step_completion'
          };

          const mutation = `
            mutation CompleteStepInProcessRun($processRunId: Int!, $onboardingId: Int!, $stepId: String!, $contextData: String) {
              completeStepInProcessRun(
                processRunId: $processRunId,
                onboardingId: $onboardingId,
                stepId: $stepId,
                contextData: $contextData
              ) {
                id
                processRunId
                onboardingId
                stepId
                completedBy
                completedAt
              }
            }
          `;

          // Check if onboardingId is available
          if (!this.onboardingData.id) {
            console.error('onboardingData.id is missing:', this.onboardingData);
            this.$vs.notify({
              title: 'Error',
              text: 'Onboarding ID is missing. Cannot track step completion.',
              color: 'danger'
            });
            return;
          }

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: mutation,
            variables: {
              processRunId: parseInt(this.currentProcessRun.id),
              onboardingId: parseInt(this.onboardingData.id),
              stepId: stepId,
              contextData: JSON.stringify(contextData)
            }
          });

          if (response && response.data && response.data.data && response.data.data.completeStepInProcessRun) {
            console.log('Step completed in ProcessRun:', stepId);
          }
        } catch (error) {
          console.error('Error completing step in ProcessRun:', error);
        }
      }
    }
  }
</script>

<style lang="scss" scoped>

  .fas.fa-trash, .fas.fa-pen {
    display: none;
  }

  .step-tools{
      display:none;
  }

  .onboarding-slideshow-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: #f8f9fa;
    z-index: 9999;
    overflow: hidden;
    /* Ensure it covers the entire screen even with zoom */
    min-width: 100vw;
    min-height: 100vh;
  }

  .fullscreen-swiper {
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px;
    box-sizing: border-box;
  }

  .slide-content {
    width: 100%;
    max-width: 1200px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    color: #333;
    position: relative;
  }

  /* Title Slide */
  .title-slide {
    background: white;
  }

  .title-content {
    .title-wrapper {
      max-width: 800px;
    }

    .main-title {
      font-size: 4rem;
      font-weight: 900;
      margin: 0 0 2rem 0;
      line-height: 1.2;
      background: linear-gradient(135deg, var(--primary), black);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;

      @media (max-width: 768px) {
        font-size: 2.5rem;
      }
    }

    .main-description {
      font-size: 1.5rem;
      font-weight: 400;
      margin: 0 0 3rem 0;
      line-height: 1.6;
      color: #6c757d;

      @media (max-width: 768px) {
        font-size: 1.2rem;
      }
    }
  }

  /* Step Slides */
  .step-slide {
    background: white;
  }

  /* Completion Slide */
  .completion-slide {
    background: white;
  }

  .completion-content {
    width: 100%;
    max-width: 800px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 40px;
    box-sizing: border-box;
  }

  .completion-wrapper {
    max-width: 600px;
  }

  .completion-icon {
    font-size: 6rem;
    color: #28a745;
    margin-bottom: 2rem;
  }

  .completion-title {
    font-size: 3.5rem;
    font-weight: 900;
    margin: 0 0 1.5rem 0;
    line-height: 1.2;
    color: #2c3e50;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  .completion-message {
    font-size: 1.5rem;
    font-weight: 400;
    margin: 0 0 3rem 0;
    line-height: 1.6;
    color: #6c757d;

    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }

  .start-over-button {
    background: #6c757d;
    border: 2px solid #6c757d;
    color: white;
    font-size: 1.3rem;
    font-weight: 600;
    padding: 15px 40px;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-right: 15px;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 768px) {
      font-size: 1.1rem;
      padding: 12px 30px;
    }
  }

  .completion-button {
    background: #28a745;
    border: 2px solid #28a745;
    color: white;
    font-size: 1.3rem;
    font-weight: 600;
    padding: 15px 40px;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 768px) {
      font-size: 1.1rem;
      padding: 12px 30px;
    }
  }

  .completion-buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;
    margin-bottom: 2rem;
  }

  .steal-sop-section {
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    text-align: center;
  }

  .steal-sop-text {
    color: rgba(255, 255, 255, 0.7);
    font-size: 1rem;
    margin-bottom: 1rem;
  }

  .steal-sop-button {
    background: transparent;
    border: 2px solid #a858f7;
    color: #a858f7;
    font-size: 1.1rem;
    font-weight: 600;
    padding: 12px 30px;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 10px;

    i {
      font-size: 1rem;
    }

    &:hover {
      background: #a858f7;
      color: white;
      transform: scale(1.05);
      box-shadow: 0 8px 25px rgba(168, 88, 247, 0.4);
    }

    @media (max-width: 768px) {
      font-size: 1rem;
      padding: 10px 25px;
    }
  }

  .step-content {
    width: 100%;
    max-width: 1200px;
    height: calc(100% - 100px);
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    text-align: center;
    padding: 40px;
    box-sizing: border-box;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    
    /* Transparent scrollbar by default, visible on hover */
    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0) transparent;
    
    &:hover {
      scrollbar-color: rgba(0, 0, 0, 0.3) transparent;
    }
    
    /* Webkit (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0);
      border-radius: 4px;
      transition: background 0.3s ease;
    }
    
    &:hover::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.3);
    }
  }

  .step-header {
    display: flex;
    margin-bottom: 4em;
    margin-top: 2em;
    width: 100%;
    flex-shrink: 0;

    .step-title {
      font-size: 3.5rem;
      font-weight: 900;
      margin: 0;
      line-height: 1.2;
      background: linear-gradient(135deg, var(--primary), black);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;

      @media (max-width: 768px) {
        font-size: 2.5rem;
      }
    }
  }

  .step-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-shrink: 0;

    .step-description {
      font-size: 1.5rem;
      font-weight: 400;
      line-height: 1.6;
      margin-bottom: 2rem;
      max-width: 800px;
      color: #6c757d;
      text-align: left;

      /* Preserve HTML formatting */
      p {
        margin-bottom: 1rem;
        
        &:last-child {
          margin-bottom: 0;
        }
      }

      ul, ol {
        margin: 1rem 0;
        padding-left: 0;
        list-style-position: inside;
        text-align: left;
        
        li {
          margin-bottom: 0.5rem;
          text-align: left;
        }
      }

      h1, h2, h3, h4, h5, h6 {
        margin: 1.5rem 0 1rem 0;
        font-weight: 600;
      }

      strong, b {
        font-weight: 600;
      }

      em, i {
        font-style: italic;
      }

      br {
        line-height: 1.6;
      }

      @media (max-width: 768px) {
        font-size: 1.2rem;
      }
    }

    .step-images {
      max-width: 100%;
      flex-shrink: 0;

      .image-gallery {
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-top: 2rem;
        width: 100%;
      }

      .step-image {
        max-width: 100%;
        max-height: 50vh;
        object-fit: contain;
        border-radius: 12px;
        cursor: pointer;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        border: 1px solid #e9ecef;

        &:hover {
          transform: scale(1.02);
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3);
        }
      }
    }
  }


  .slide-navigation {
    position: sticky;
    bottom: 30px;
    margin-top: 2em;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    display:none;
    z-index: 10;
  }

  .slide-counter {
    background: #f8f9fa;
    padding: 12px 24px;
    border-radius: 25px;
    border: 1px solid #dee2e6;
    color: #6c757d;

    .current-slide {
      font-size: 1.2rem;
      font-weight: bold;
      color: var(--primary);
    }

    .total-slides {
      font-size: 1rem;
      opacity: 0.8;
    }
  }

  .swiper-button-prev, .swiper-container-rtl .swiper-button-next,
  .swiper-button-next, .swiper-container-rtl .swiper-button-prev {
    background: none !important;
    background-image: none !important;
  }

  /* Navigation Buttons */
  .swiper-button-prev,
  .swiper-button-next {
    bottom: 30px !important;
    width: 50px;
    height: 50px;
    /*background: var(--primary);*/
    border-radius: 50%;
    border: none;
    color: white;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 20px;
    color: var(--primary);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    top: unset !important;
    font-size: 20px;
    font-weight: 900;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    }

    &::after {
      font-size: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .swiper-button-next {
    right: 10px;
  }

  .swiper-button-prev {
    left: 10px;
  }

  .swiper-button-next.disabled {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }

  /* Pagination - Removed */

  /* Floating Controls */

  .tcvelik-button {
    position: fixed;
    top: 20px;
    right: 90px;
    display: flex;
    gap: 10px;
    z-index: 10000;
  }

  /* TCVelik Branding */
  .tcvelik-branding {
    position: fixed;
    top: 20px;
    left: 50px;
    /* -webkit-transform: translateX(-50%); */
    /* transform: translateX(-50%); */
    z-index: 10000;
  }

  .branding-link {
    display: flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
    color: #6c757d;
    font-size: 0.9rem;
    font-weight: 400;
    transition: all 0.3s ease;
    padding: 8px 12px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:hover {
      color: var(--primary);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      text-decoration: none;
    }

    .branding-text {
      opacity: 0.8;
    }

    .branding-logo {
      font-weight: 700;
      color: var(--primary);
      font-size: 0.95rem;
    }
  }

  .floating-controls {
    position: fixed;
    top: 20px;
    right: 20px;
    display: flex;
    gap: 10px;
    z-index: 10000;
  }

  .control-btn {
    width: 50px;
    height: 50px;
    background: transparent;
    border: none;
    border-radius: 50%;
    color: black;
    font-size: 1.2rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  /* Loading State */
  .loading-state {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    z-index: 9998;
  }

  .loading-content {
    text-align: center;
    color: #6c757d;

    i {
      font-size: 3rem;
      margin-bottom: 1rem;
      color: var(--primary);
    }

    p {
      font-size: 1.2rem;
      margin: 0;
    }
  }

  /* Image Modal */
  .image-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10001;
    cursor: pointer;
  }

  .modal-content {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;

    .modal-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 8px;
    }

    .modal-close {
      position: absolute;
      top: -50px;
      right: 0;
      background: #6c757d;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      color: white;
      font-size: 1.2rem;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: #5a6268;
        transform: scale(1.1);
      }
    }
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .swiper-slide {
      padding: 5px;
    }

    .step-content {
      padding: 20px;
    }

    .step-header {
      flex-direction: column;
      
    }

    .floating-controls {
      top: 10px;
      right: 10px;
    }

    .control-btn {
      width: 40px;
      height: 40px;
      font-size: 1rem;
    }

    .tcvelik-button {
      top: 10px;
      right: 70px;
    }

    .tcvelik-branding {
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%); /* Center horizontally on mobile */
    }

    .branding-link {
      font-size: 0.8rem;
      padding: 6px 10px;
    }
  }

  @media (max-width: 480px) {
    .main-title {
      font-size: 2rem !important;
    }

    .main-description {
      font-size: 1rem !important;
    }

    .step-title {
      font-size: 2rem !important;
    }

    .step-description {
      font-size: 1.1rem !important;
    }

    .completion-title {
      font-size: 2rem !important;
    }

    .completion-message {
      font-size: 1rem !important;
    }

    .completion-icon {
      font-size: 4rem !important;
    }

    .tcvelik-button {
      top: 10px;
      right: 70px;
    }

    .tcvelik-branding {
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%); /* Center horizontally on small mobile */
    }

    .branding-link {
      font-size: 0.75rem;
      padding: 5px 8px;
    }
  }


  /* Form Modal Styling */
  .form-modal-content {
    padding: 20px;
    max-width: 600px;
    margin: 0 auto;
  }

  .form-container {
    width: 100%;
  }

  .form-title {
    font-size: 1.8rem;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 1rem;
    text-align: center;
  }

  .form-description {
    font-size: 1rem;
    color: #64748b;
    margin-bottom: 2rem;
    text-align: center;
    line-height: 1.5;
  }

  .dynamic-form {
    width: 100%;
  }

  .form-field {
    margin-bottom: 1.5rem;
  }

  .field-label {
    display: block;
    font-size: 1rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .field-input {
    width: 100%;
  }

  .form-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
  }

  .form-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }

  /* Field type specific styles */
  .checkbox-field {
    margin-top: 0.5rem;
  }

  .file-field {
    margin-top: 0.5rem;
  }

  .file-field small {
    display: block;
    margin-top: 0.25rem;
    font-style: italic;
  }

  /* SOP Selection Styles */
  .sop-selection {
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 1rem;
    background-color: #f9fafb;
  }

  .no-sop-selected {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .no-sop-text {
    color: #6b7280;
    font-style: italic;
  }

  .sop-selected {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .sop-info {
    flex: 1;
  }

  .sop-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
  }

  .sop-description {
    color: #6b7280;
    font-size: 0.9rem;
    margin: 0;
    line-height: 1.4;
  }

  .change-link {
    color: #3b82f6;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .change-link:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }

  /* SOP Selector Modal Styles */
  .sop-selector-content {
    padding: 1rem;
    height: 100%;
    overflow-y: auto;
  }

  .sop-list-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .sop-list-header {
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 1rem;
  }

  .sop-table-container {
    flex: 1;
    overflow: auto;
  }

  .sop-table {
    margin: 0;
  }

  .sop-table .vs-table--tbody .vs-table--tr {
    border-bottom: 1px solid #f3f4f6;
  }

  .sop-table .vs-table--tbody .vs-table--tr:hover {
    background-color: #f9fafb;
  }

  .sop-table .vs-table--thead .vs-table--th {
    background-color: #f8fafc;
    font-weight: 600;
    color: #374151;
    border-bottom: 2px solid #e5e7eb;
  }

  .sop-id {
    font-weight: 500;
    color: #6b7280;
  }

  .sop-name strong {
    color: #1f2937;
  }

  .sop-description {
    color: #6b7280;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .empty-sops {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  /* Assign SOP Step Styling */
  .assign-sop-step {
    margin: 2rem 0;
    padding: 2rem;
    background: rgba(156, 39, 176, 0.1);
    border: 2px solid #9c27b0;
    border-radius: 12px;
    text-align: center;
  }

  .assign-sop-step h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #7b1fa2;
    margin-bottom: 1rem;
  }

  .assign-sop-step p {
    font-size: 1rem;
    color: #9c27b0;
    margin-bottom: 1.5rem;
    line-height: 1.4;
  }

  /* Assign SOP Modal Styling */
  .assign-sop-modal-content {
    padding: 20px;
    max-width: 600px;
    margin: 0 auto;
  }

  .assign-sop-container {
    width: 100%;
  }

  .assign-sop-title {
    font-size: 1.8rem;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 1rem;
    text-align: center;
  }

  .assign-sop-description {
    font-size: 1rem;
    color: #64748b;
    margin-bottom: 2rem;
    text-align: center;
    line-height: 1.5;
  }

  .assign-sop-form {
    width: 100%;
  }

  /* Date type selection styles */
  .date-type-selection {
    display: flex;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  .radio-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: #374151;
  }

  .radio-option input[type="radio"] {
    margin: 0;
    cursor: pointer;
  }

  .relative-date-input {
    margin-top: 0.5rem;
  }

  .relative-text {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .assign-sop-form .form-field {
    margin-bottom: 1.5rem;
  }

  .assign-sop-form .field-label {
    display: block;
    font-size: 1rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .assign-sop-form .field-input {
    width: 100%;
  }

  .assign-sop-form .form-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
  }

  .assign-sop-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }

  /* Assign SOP Step Styles */
  .assign-sop-step {
    padding: 1.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    background-color: #f9fafb;
    margin: 1rem 0;
  }

  .assign-sop-success {
    text-align: center;
    padding: 2rem;
  }

  .success-message {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .assign-sop-editable {
    max-width: 500px;
  }

  .assign-sop-editable h3 {
    color: #1f2937;
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .assign-sop-editable p {
    color: #6b7280;
    margin-bottom: 1.5rem;
  }

  .assign-sop-editable .form-field {
    margin-bottom: 1rem;
  }

  .assign-sop-editable .field-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .assign-sop-editable .field-input {
    width: 100%;
  }

  .assign-sop-editable .form-actions {
    margin-top: 1.5rem;
    display: flex;
    justify-content: flex-start;
  }
</style>
